import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import AuthShell from "../components/AuthShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Set a new password" description="Set a new StUpEco Hub password." path="/reset-password" />
      <AuthShell
        title="Set a new password"
        subtitle={token ? "Choose a strong new password for your account." : "This reset link is missing or invalid."}
        footer={<>Go back to <Link to="/login" className="font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline">login</Link></>}
      >
        {token ? (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form" noValidate>
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" data-testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="h-11 rounded-xl" />
            </div>
            <button type="submit" disabled={submitting} data-testid="reset-submit" className="group inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-70">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : <>Update password <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
            </button>
          </form>
        ) : (
          <Link to="/forgot-password" data-testid="reset-request-new" className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white">
            Request a new link <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </AuthShell>
    </>
  );
}
